import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Fragment, useEffect, useMemo, useState } from "react";

import { cn } from "../lib/utils";

function downloadCsv(filename, rows) {
  const headers = [
    "Control ID",
    "Control Domain",
    "Control Requirement",
    "Min Implementation Standard",
    "Evidence / Artifacts",
    "Owner",
    "Frequency",
    "Technology Enablers",
    "Audit/Test Method",
    "Common Gaps",
    "Severity",
    "Automation Potential",
  ];

  const escape = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;

  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.controlId,
        r.controlDomain,
        r.controlRequirement,
        r.minimumImplementationStandard,
        r.evidenceArtifacts,
        r.owner,
        r.frequency,
        r.technologyEnablers,
        r.auditTestMethod,
        r.commonGaps,
        r.severity,
        r.automationPotential,
      ]
        .map(escape)
        .join(","),
    ),
  ].join("\n");

  const blob = new Blob([lines], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function severityPill(sev) {
  const base = "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold";
  if (sev === "Critical") return `${base} bg-[rgba(239,68,68,0.15)] text-[color:var(--accent-critical)] border border-border-subtle`;
  if (sev === "High") return `${base} bg-[rgba(245,158,11,0.14)] text-[color:var(--accent-amber)] border border-border-subtle`;
  if (sev === "Medium") return `${base} bg-[rgba(59,130,246,0.12)] text-[color:var(--accent-blue)] border border-border-subtle`;
  return `${base} bg-white/5 text-text-secondary border border-border-subtle`;
}

function automationPill(v) {
  const base = "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border border-border-subtle";
  if (v === "High") return `${base} bg-[rgba(16,185,129,0.12)] text-[color:var(--accent-compliance)]`;
  if (v === "Medium") return `${base} bg-[rgba(245,158,11,0.12)] text-[color:var(--accent-amber)]`;
  return `${base} bg-white/5 text-text-secondary`;
}

export function ControlCatalogTable({
  data,
  className,
  initialDomain,
  onDomainChange,
}) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState(
    initialDomain ? [{ id: "controlDomain", value: initialDomain }] : [],
  );
  const [columnVisibility, setColumnVisibility] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [columnsOpen, setColumnsOpen] = useState(false);

  const domainOptions = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.controlDomain))).sort();
  }, [data]);

  const severityOptions = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.severity))).sort();
  }, [data]);

  const automationOptions = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.automationPotential))).sort();
  }, [data]);

  useEffect(() => {
    if (!initialDomain) return;
    setColumnFilters((prev) => {
      const next = prev.filter((f) => f.id !== "controlDomain");
      next.push({ id: "controlDomain", value: initialDomain });
      return next;
    });
  }, [initialDomain]);

  const columns = useMemo(
    () => [
      {
        id: "expander",
        header: "",
        cell: ({ row }) => (
          <button
            type="button"
            className="focus-ring rounded-lg px-2 py-1 text-text-secondary hover:bg-white/5"
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? "–" : "+"}
          </button>
        ),
        size: 44,
      },
      {
        accessorKey: "controlId",
        header: "Control ID",
        size: 120,
        cell: ({ getValue }) => (
          <span className="font-mono text-xs font-semibold text-text-primary">
            {String(getValue())}
          </span>
        ),
      },
      {
        accessorKey: "controlDomain",
        header: "Control Domain",
        size: 180,
      },
      {
        accessorKey: "controlRequirement",
        header: "Control Requirement",
        size: 360,
      },
      {
        accessorKey: "minimumImplementationStandard",
        header: "Min Implementation Standard",
        size: 320,
      },
      {
        accessorKey: "evidenceArtifacts",
        header: "Evidence / Artifacts",
        size: 260,
      },
      {
        accessorKey: "owner",
        header: "Owner",
        size: 140,
      },
      {
        accessorKey: "frequency",
        header: "Frequency",
        size: 140,
      },
      {
        accessorKey: "technologyEnablers",
        header: "Technology Enablers",
        size: 260,
      },
      {
        accessorKey: "auditTestMethod",
        header: "Audit/Test Method",
        size: 260,
      },
      {
        accessorKey: "commonGaps",
        header: "Common Gaps",
        size: 260,
      },
      {
        accessorKey: "severity",
        header: "Severity",
        size: 120,
        cell: ({ getValue }) => (
          <span className={severityPill(String(getValue()))}>{String(getValue())}</span>
        ),
      },
      {
        accessorKey: "automationPotential",
        header: "Automation",
        size: 140,
        cell: ({ getValue }) => (
          <span className={automationPill(String(getValue()))}>{String(getValue())}</span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        size: 120,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="focus-ring rounded-lg border border-border-subtle bg-white/5 px-2 py-1 text-xs font-semibold text-text-primary hover:bg-white/10"
              onClick={() => row.toggleExpanded()}
            >
              ↗
            </button>
            <button
              type="button"
              className="focus-ring rounded-lg border border-border-subtle bg-white/5 px-2 py-1 text-xs font-semibold text-text-primary hover:bg-white/10"
              onClick={async () => {
                await navigator.clipboard.writeText(
                  `${row.original.controlId}: ${row.original.controlRequirement}`,
                );
              }}
            >
              📋
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      pagination,
    },
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    onSortingChange: setSorting,
    onColumnFiltersChange: (next) => {
      setColumnFilters(next);
      const domainFilter = (Array.isArray(next) ? next : [])
        .find((f) => f.id === "controlDomain")?.value;
      onDomainChange?.(typeof domainFilter === "string" ? domainFilter : null);
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const visibleRows = table.getRowModel().rows;

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-1 items-center gap-3">
          <input
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Search controls…"
            className="focus-ring h-11 w-full rounded-xl border border-border-subtle bg-white/5 px-3 text-sm text-text-primary placeholder:text-text-tertiary"
          />
          <button
            type="button"
            className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
            onClick={() => {
              setGlobalFilter("");
              setSorting([]);
              setColumnFilters([]);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
              onDomainChange?.(null);
            }}
          >
            Clear filters
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={
              (columnFilters.find((f) => f.id === "controlDomain")
                ?.value) ?? ""
            }
            onChange={(e) => {
              const val = e.target.value;
              setColumnFilters((prev) => {
                const next = prev.filter((f) => f.id !== "controlDomain");
                if (val) next.push({ id: "controlDomain", value: val });
                return next;
              });
              setPagination((p) => ({ ...p, pageIndex: 0 }));
              onDomainChange?.(val || null);
            }}
            className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm font-semibold text-text-primary"
          >
            <option value="">All domains</option>
            {domainOptions.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>

          <select
            value={
              (columnFilters.find((f) => f.id === "severity")
                ?.value) ?? ""
            }
            onChange={(e) => {
              const val = e.target.value;
              setColumnFilters((prev) => {
                const next = prev.filter((f) => f.id !== "severity");
                if (val) next.push({ id: "severity", value: val });
                return next;
              });
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm font-semibold text-text-primary"
          >
            <option value="">All severity</option>
            {severityOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <select
            value={
              (columnFilters.find((f) => f.id === "automationPotential")
                ?.value) ?? ""
            }
            onChange={(e) => {
              const val = e.target.value;
              setColumnFilters((prev) => {
                const next = prev.filter((f) => f.id !== "automationPotential");
                if (val) next.push({ id: "automationPotential", value: val });
                return next;
              });
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm font-semibold text-text-primary"
          >
            <option value="">All automation</option>
            {automationOptions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
            onClick={() => downloadCsv("rbi-control-catalog.csv", visibleRows.map((r) => r.original))}
          >
            Export CSV
          </button>
          <button
            type="button"
            className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-secondary"
          >
            Export PDF
          </button>
          <button
            type="button"
            className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-secondary"
          >
            Export Excel
          </button>

          <div className="relative">
            <button
              type="button"
              className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
              onClick={() => setColumnsOpen((v) => !v)}
            >
              Columns ▾
            </button>
            {columnsOpen && (
              <div className="absolute right-0 top-12 z-20 w-64 rounded-2xl border border-border-subtle bg-background-tertiary/90 p-3 shadow-glass backdrop-blur-2xl">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Toggle columns
                </div>
                <div className="mt-2 space-y-2">
                  {table
                    .getAllLeafColumns()
                    .filter((c) => !["expander"].includes(c.id))
                    .map((col) => (
                      <label
                        key={col.id}
                        className="flex cursor-pointer items-center justify-between rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary hover:bg-white/10"
                      >
                        <span>{String(col.columnDef.header ?? col.id)}</span>
                        <input
                          type="checkbox"
                          checked={col.getIsVisible()}
                          onChange={col.getToggleVisibilityHandler()}
                        />
                      </label>
                    ))}
                </div>
                <button
                  type="button"
                  className="mt-3 w-full rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm font-semibold text-text-primary hover:bg-white/10"
                  onClick={() => setColumnsOpen(false)}
                >
                  Close
                </button>
              </div>
            )}
          </div>

          <select
            value={pagination.pageSize}
            onChange={(e) =>
              setPagination({ pageIndex: 0, pageSize: Number(e.target.value) })
            }
            className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm font-semibold text-text-primary"
          >
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1400px] w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-10 bg-background-tertiary/90 backdrop-blur-xl">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className="relative border-b border-border-subtle px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted"
                      style={{ width: header.getSize() }}
                    >
                      <button
                        type="button"
                        className="inline-flex items-center gap-2"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        {header.column.getIsSorted() === "asc" && "▲"}
                        {header.column.getIsSorted() === "desc" && "▼"}
                      </button>
                      {header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={cn(
                            "absolute right-0 top-0 h-full w-1 cursor-col-resize select-none",
                            header.column.getIsResizing() && "bg-white/20",
                          )}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <tr
                    className="border-b border-border-subtle hover:bg-white/5"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="border-b border-border-subtle px-3 py-3 align-top text-sm text-text-secondary"
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <tr>
                      <td
                        colSpan={row.getVisibleCells().length}
                        className="border-b border-border-subtle bg-white/3 px-4 py-4"
                      >
                        <div className="grid gap-3 md:grid-cols-2">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                              Minimum implementation standard
                            </div>
                            <div className="mt-1 text-sm text-text-secondary">
                              {row.original.minimumImplementationStandard}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                              Evidence / artifacts
                            </div>
                            <div className="mt-1 text-sm text-text-secondary">
                              {row.original.evidenceArtifacts}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                              Audit / test method
                            </div>
                            <div className="mt-1 text-sm text-text-secondary">
                              {row.original.auditTestMethod}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                              Common gaps
                            </div>
                            <div className="mt-1 text-sm text-text-secondary">
                              {row.original.commonGaps}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-border-subtle px-4 py-4 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-text-tertiary">
            Showing {table.getRowModel().rows.length} controls (filtered)
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="focus-ring h-10 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm font-semibold text-text-primary hover:bg-white/10 disabled:opacity-40"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Prev
            </button>
            <button
              type="button"
              className="focus-ring h-10 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm font-semibold text-text-primary hover:bg-white/10 disabled:opacity-40"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <div className="text-xs text-text-tertiary">
        Tip: horizontal scroll is enabled on smaller screens.
      </div>
    </div>
  );
}
